import React from 'react';
import { Field, Section } from '@h2/types';
import { Label } from '@h2/ui';
import { useConfigurationStore } from '../../stores/useConfigurationStore';
import { TextInput } from './fields/TextInput';
import { TextareaInput } from './fields/TextareaInput';
import { NumberInput } from './fields/NumberInput';
import { SelectInput } from './fields/SelectInput';
import { CheckboxInput } from './fields/CheckboxInput';
import { ToggleInput } from './fields/ToggleInput';
import { RadioInput } from './fields/RadioInput';
import { PresetSelector } from './PresetSelector';
import { PresetTagsField } from './PresetTagsField';
import { SimpleTagsField } from './SimpleTagsField';
import { LanguageSettingsStep } from '../work/steps/LanguageSettingsStep';
import { WorkflowConfigurationStep } from '../work/steps/WorkflowConfigurationStep';
import { WorkerAssignmentStep } from '../work/steps/WorkerAssignmentStep';
import { FinalConfirmationStep } from '../work/steps/FinalConfirmationStep';
import { TaskTypeEditorOptionsField } from './TaskTypeEditorOptionsField';
import { TaskTypeLanguageCheckbox } from './TaskTypeLanguageCheckbox';
import { TaskTypeColorPicker } from './TaskTypeColorPicker';
import { TaskTypeIconPicker } from './TaskTypeIconPicker';
import { PrioritySelector } from '../ui/PrioritySelector';

interface ConfigurationFieldProps {
  field: Field;
  section?: Section;
  hideTitle?: boolean;
}

export const ConfigurationField: React.FC<ConfigurationFieldProps> = ({ field, section, hideTitle = false }) => {
  const { formData, errors, updateField } = useConfigurationStore();

  const value = formData[field.id] || field.value || '';
  const error = errors[field.id] || '';

  const handleChange = (newValue: any) => {
    updateField(field.id, newValue);
  };

  const renderFieldInput = () => {
    const getTypedValue = (fieldType: string) => {
      switch (fieldType) {
        case 'number':
          return typeof value === 'number' ? value : (parseFloat(String(value)) || 0);
        case 'checkbox':
        case 'toggle':
          return typeof value === 'boolean' ? value : Boolean(value);
        default:
          return typeof value === 'string' ? value : String(value || '');
      }
    };

    const typedValue = getTypedValue(field.type);
    const errorString = typeof error === 'string' ? error : undefined;

    switch (field.type) {
      case 'text':
        return <TextInput field={field} value={typedValue as string} onChange={handleChange} error={errorString} />;
      case 'textarea':
        return <TextareaInput field={field} value={typedValue as string} onChange={handleChange} error={errorString} />;
      case 'number':
        return <NumberInput field={field} value={typedValue as number} onChange={handleChange} error={errorString} />;
      case 'select':
        return <SelectInput field={field} value={typedValue as string} onChange={handleChange} error={errorString} />;
      case 'radio':
        return <RadioInput field={field} value={typedValue as string} onChange={handleChange} error={errorString} />;
      case 'checkbox':
        return <CheckboxInput field={field} value={typedValue as boolean} onChange={handleChange} error={errorString} />;
      case 'toggle':
        return <ToggleInput field={field} value={typedValue as boolean} onChange={handleChange} error={errorString} />;
      case 'color':
        return (
          <input
            type="color"
            value={typedValue as string}
            onChange={(e) => handleChange(e.target.value)}
            className="w-16 h-10 border border-input rounded-md cursor-pointer"
          />
        );
      case 'preset-selector':
        return <PresetSelector 
          expectedSourceLanguage={field.customProps?.expectedSourceLanguage}
          expectedTargetLanguages={field.customProps?.expectedTargetLanguages}
        />;
      case 'priority-selector':
        return <PrioritySelector 
          value={value as string[]}
          onChange={handleChange}
          isPremium={formData['isPremiumWork'] === 'premium'}
        />;
    case 'preset-tags':
      return <PresetTagsField fieldId={field.id} />;
    case 'simple-tags':
      return <SimpleTagsField fieldId={field.id} />;
    case 'language-settings':
      return <LanguageSettingsStep 
        title={section?.title} 
        description={section?.description} 
        hideTitle={hideTitle}
        isLanguageDisabled={field.customProps?.isLanguageDisabled || false}
        preSelectedTargetLanguages={field.customProps?.preSelectedTargetLanguages || []}
      />;
    case 'workflow-configuration':
      return <WorkflowConfigurationStep title={section?.title} description={section?.description} hideTitle={hideTitle} />;
    case 'worker-assignment':
      return <WorkerAssignmentStep title={section?.title} description={section?.description} hideTitle={hideTitle} />;
    case 'final-confirmation':
      return <FinalConfirmationStep title={section?.title} description={section?.description} hideTitle={hideTitle} />;
    case 'task-type-editor-options':
      return <TaskTypeEditorOptionsField title={section?.title} description={section?.description} hideTitle={hideTitle} />;
    case 'task-type-language-checkbox':
      return <TaskTypeLanguageCheckbox title={section?.title} description={section?.description} hideTitle={hideTitle} />;
    case 'task-type-color-picker':
      return <TaskTypeColorPicker title={section?.title} description={section?.description} hideTitle={hideTitle} />;
    case 'task-type-icon-picker':
      return <TaskTypeIconPicker />;
      default:
        return <TextInput field={field} value={typedValue as string} onChange={handleChange} error={errorString} />;
    }
  };

  return (
    <div className="space-y-2">
      {!['preset-selector', 'priority-selector', 'preset-tags', 'simple-tags', 'language-settings', 'workflow-configuration', 'worker-assignment', 'final-confirmation', 'task-type-editor-options', 'task-type-language-checkbox', 'task-type-icon-picker', 'task-type-color-picker'].includes(field.type) && (
        <Label htmlFor={field.id} className="text-sm font-medium">
          {field.label}
          {field.validation?.rules?.some(rule => rule.type === 'required') && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </Label>
      )}
      
      {field.description && (
        <p className="text-xs text-gray-500">
          {field.description}
        </p>
      )}
      
      {renderFieldInput()}
      
      {error && typeof error === 'string' && (
        <p className="text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};
